import type { ScaleOrdinal } from 'd3-scale';
import { useCallback, useMemo } from 'react';

import { getXYLegendActions } from './utils/getXYLegendActions.js';
import {
  AREA_CHART_ICON_SHAPE,
  BAR_CHART_ICON_SHAPE,
  LINE_CHART_ICON_SHAPE,
  RECT_CHART_ICON_SHAPE,
  XYLegendIcon,
} from './XYLegendIcon.js';
import { useChartLayoutLoading } from '../../../../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { getLegendOrientation } from '../../../../core/components/chart-layout/utils/orientation.js';
import { ChartLegend } from '../../../../core/components/legend/categorical/ChartLegend.js';
import type { LegendItem } from '../../../../core/components/legend/categorical/types/legend.js';
import { usePinCoordinationActions } from '../../../../core/components/overlay-tooltip/usePinCoordinationActions.js';
import { useTooltipActions } from '../../../../core/components/overlay-tooltip/useTooltipActions.js';
import { buildCategoricalItemsFromScale } from '../../../../core/utils/build-categorical-items-from-scale.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { SeriesIdToColorScale } from '../../../context/XYChartColorScales.context.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartTruncationMode } from '../../../hooks/useXYChartTruncationMode.js';
import { reduceFilterSeries } from '../../../store/reducer-filter-series.js';
import { useSetState } from '../../../store/selectors.js';
import {
  AREA_VARIANT,
  type AreaSeriesWithDatapoints,
  BAR_VARIANT,
  type BarSeriesWithDatapoints,
  DOT_VARIANT,
  type DotSeriesWithDatapoints,
  LINE_VARIANT,
  type LineSeriesWithDatapoints,
  RECT_VARIANT,
  type RectSeriesWithDatapoints,
  type SeriesToMetadata,
  type XYChartSeriesWithDatapoints,
} from '../../../types/xy-chart-internals.js';
import { isRangeLegend } from '../../../utils/is-range-legend.js';
import { isRectSeries } from '../../../utils/type-guards.js';

const CHART_ICONS = {
  [LINE_VARIANT]: LINE_CHART_ICON_SHAPE,
  [BAR_VARIANT]: BAR_CHART_ICON_SHAPE,
  [RECT_VARIANT]: RECT_CHART_ICON_SHAPE,
  [AREA_VARIANT]: AREA_CHART_ICON_SHAPE,
} as const;

function getLegendItems(
  series: (
    | DotSeriesWithDatapoints
    | LineSeriesWithDatapoints
    | BarSeriesWithDatapoints
    | RectSeriesWithDatapoints
    | AreaSeriesWithDatapoints
  )[],
  metadata: SeriesToMetadata,
  isMixedSeries: boolean,
  scaleColorMap: SeriesIdToColorScale,
  isCategoricalRectOnly: boolean,
) {
  return series.flatMap((series) => {
    if (series.type === RECT_VARIANT) {
      if (isRangeLegend(metadata, series)) {
        return [];
      }
      const scale = safeGet(scaleColorMap, series.id) as ScaleOrdinal<
        string,
        string
      >;
      const overrideItems = safeGet(metadata, series).categoricalColorOverrides;

      const shape = isCategoricalRectOnly ? 'circle' : CHART_ICONS[series.type];

      const items = buildCategoricalItemsFromScale(
        scale,
        overrideItems,
        //We need to compare exactly the same id when an action is selected
        //In RectSeries, categories are unique per series.
        (category: string) => `${series.id}--${category}`,
      );

      return items.map((item: LegendItem) => ({
        ...item,
        icon: <XYLegendIcon color={item.color.Default} shape={shape} />,
      }));
    } else {
      const { name, color, shape = 'circle' } = safeGet(metadata, series);
      const iconShape =
        isMixedSeries && series.type !== DOT_VARIANT
          ? CHART_ICONS[series.type]
          : shape;

      return {
        id: series.id,
        color,
        name,
        icon: <XYLegendIcon color={color.Default} shape={iconShape} />,
      };
    }
  });
}

interface XYLegendProps {
  series: XYChartSeriesWithDatapoints[];
  seriesToMetadata: SeriesToMetadata;
}

export function XYLegend({ series, seriesToMetadata }: XYLegendProps) {
  const { reset } = useTooltipActions();
  const { dismissAnnotation } = usePinCoordinationActions();
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const orientation = getLegendOrientation(layoutOrientation);
  const setState = useSetState();
  const isLoading = useChartLayoutLoading();
  const scaleColorMap = useXYChartColorScales();

  const { truncationMode } = useXYChartTruncationMode();

  const firstSeries = series[0];
  const isCategoricalRectOnly =
    isRectSeries(firstSeries) &&
    series.every(isRectSeries) &&
    !isRangeLegend(seriesToMetadata, firstSeries);

  const isMixedSeries = useMemo(() => {
    const typeSet = new Set(series.map((s) => s.type));
    return typeSet.size >= 2;
  }, [series]);

  const legendItems = useMemo(
    () =>
      getLegendItems(
        series,
        seriesToMetadata,
        isMixedSeries,
        scaleColorMap,
        isCategoricalRectOnly,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, seriesToMetadata],
  );

  const legendActions = getXYLegendActions(series, seriesToMetadata);

  const updateVisibleSeries = useCallback(
    (visibleItems: LegendItem[]) => {
      const visibleIds = new Set(visibleItems.map((item) => item.id));

      reset();
      dismissAnnotation();
      setState(reduceFilterSeries(visibleIds));
    },
    [setState, reset, dismissAnnotation],
  );

  const updateHighlightedSeriesId = (legendItemId?: string) => {
    setState((prev) => ({ ...prev, highlightedSeriesId: legendItemId }));
  };

  return (
    <ChartLegend
      items={legendItems}
      orientation={orientation}
      truncationMode={truncationMode}
      interactionsDisabled={isLoading}
      onSeriesAction={legendActions}
      onHover={(legendItem) => updateHighlightedSeriesId(legendItem?.id)}
      onClick={updateVisibleSeries}
    />
  );
}
